import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../utils/toast';
import { AppSelector } from '../components/AppSelector';
import { Sidebar } from '../components/Sidebar';
import styled from 'styled-components';

// Company brand colors with enhanced glow effect
const COMPANY_BLUE = {
  from: '#2563EB',
  via: '#3B82F6',
  to: '#1D4ED8',
  shadow: 'rgba(37, 99, 235, 0.2)'
};

// Default glow for apps without specific brand colors
const DEFAULT_GLOW = {
  from: '#6366F1',
  via: '#8B5CF6',
  to: '#6366F1',
  shadow: 'rgba(99, 102, 241, 0.15)'
};

// Brand-specific color schemes
const brandColors = {
  company: COMPANY_BLUE,
  purple: {
    from: '#8B5CF6',
    via: '#7C3AED',
    to: '#6D28D9',
    shadow: 'rgba(139, 92, 246, 0.2)'
  },
  blue: {
    from: '#3B82F6',
    via: '#2563EB',
    to: '#1D4ED8',
    shadow: 'rgba(59, 130, 246, 0.2)'
  },
  green: {
    from: '#10B981',
    via: '#059669',
    to: '#047857',
    shadow: 'rgba(16, 185, 129, 0.2)'
  },
  red: {
    from: '#EF4444',
    via: '#DC2626',
    to: '#B91C1C',
    shadow: 'rgba(239, 68, 68, 0.2)'
  },
  orange: {
    from: '#F97316',
    via: '#EA580C',
    to: '#C2410C',
    shadow: 'rgba(249, 115, 22, 0.2)'
  },
  yellow: {
    from: '#EAB308',
    via: '#CA8A04',
    to: '#A16207',
    shadow: 'rgba(234, 179, 8, 0.2)'
  },
  pink: {
    from: '#EC4899',
    via: '#DB2777',
    to: '#BE185D',
    shadow: 'rgba(236, 72, 153, 0.2)'
  },
  gray: {
    from: '#4B5563',
    via: '#374151',
    to: '#1F2937',
    shadow: 'rgba(75, 85, 99, 0.2)'
  }
};

// Modern monochrome icon mapping with larger icons
const getIcon = (appId) => {
  switch (appId) {
    // Model icons
    case 'claude-3-7':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      );
    case 'gpt-o1':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'mistral-large':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case 'llama-3':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'deepseek':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      );
    case 'gpt-o3-mini':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    
    // Existing app icons
      case 'ai-toybox':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
    case 'test-script-generator':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 2v6h6" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 16h6" />
          </svg>
        );
    case 'sql-writer':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        );
    case 'pyspark-writer':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
    case 'creative-writer':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
    case 'web-crafter':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
        );
    case 'life-coach':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    case 'lesson-plan-creator':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
    case 'course-outline-creator':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    case 'social-media-manager':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      );
    case 'grant-finder':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      );
    case 'grant-writer':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'powerpoint-generator':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      );
    // Enterprise apps
      case 'salesforce':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h18v18H3V3zm4 4h4m-4 4h10m-10 4h4" />
          </svg>
        );
      case 'hubspot':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v8m-4-5v5M8 8v8m12-8v8M4 8v8" />
        </svg>
      );
    case 'zendesk':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'microsoft-365':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        );
      case 'google-workspace':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        );
    case 'zoom':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'slack':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
          </svg>
        );
    case 'jira':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
    case 'asana':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        );
      case 'monday':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
    case 'workday':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'bamboohr':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'aws':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10" />
        </svg>
      );
    case 'azure':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      );
    case 'gcp':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'databricks':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
        );
    case 'snowflake':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10M12 12v8m-4-4h8" />
          </svg>
        );
      case 'tableau':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v8m-4-5v5M8 8v8M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        </svg>
      );
      case 'sigma':
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.871 4A17.926 17.926 0 003 12c0 2.874.673 5.59 1.871 8m14.13 0a17.926 17.926 0 001.87-8c0-2.874-.673-5.59-1.87-8M9 9h1.246a1 1 0 01.961.725l1.586 5.55a1 1 0 00.961.725H15m1-7h-.08a2 2 0 00-1.519.698L9.6 15.302A2 2 0 018.08 16H8" />
          </svg>
        );
      case 'figma':
        return (
          <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 5.5v13M5 12h14M7.45 3.97C6.89 4.44 6.5 5.17 6.5 6c0 1.38 1.12 2.5 2.5 2.5h5c1.38 0 2.5-1.12 2.5-2.5S15.38 3.5 14 3.5H9c-.83 0-1.56.39-2.03.95zM6.5 18c0 1.38 1.12 2.5 2.5 2.5h5c1.38 0 2.5-1.12 2.5-2.5S15.38 15.5 14 15.5H9c-1.38 0-2.5 1.12-2.5 2.5z" />
        </svg>
      );
      case 'confluence':
        return (
          <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
      case 'linear':
        return (
          <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm8 6v5m-4-2l4 2 4-2m-8-3l4-2 4 2" />
        </svg>
      );
    case 'resume-builder':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 012 2z" />
        </svg>
      );
    case 'principal-ai':
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    default:
        return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        );
    }
  };

const AppCard = ({ app, onClick }) => {
  const navigate = useNavigate();
  
  const getAuraColors = () => {
    if (app.category === 'models') {
      // Special glow for models
      return {
        from: '#8B5CF6',
        via: '#6366F1',
        to: '#8B5CF6',
        shadow: 'rgba(139, 92, 246, 0.2)'
      };
    }
    
    if (app.brand === 'company') {
      return COMPANY_BLUE;
    }
    return brandColors[app.brand] || DEFAULT_GLOW;
  };

  const auraColors = getAuraColors();
  
  return (
    <div 
      onClick={() => onClick(app)}
      className="group relative bg-white rounded-lg border border-gray-100 p-6 min-h-[300px] cursor-pointer transition-all duration-200 hover:border-gray-200 hover:shadow-lg hover:-translate-y-1 flex flex-col"
    >
      {/* Main Content */}
      <div className="flex flex-col flex-1">
        {/* Header with Icon and Badges */}
        <div className="flex items-start justify-between mb-4">
          {/* Icon */}
          <div className={`
            flex-shrink-0 w-14 h-14 rounded-xl
            ${app.category === 'models' ? 'bg-purple-50 text-purple-600' : 
              app.brand === 'company' ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'}
            flex items-center justify-center
          `}>
            {getIcon(app.id)}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {app.category === 'models' && (
              <div className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-medium flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Model
              </div>
            )}
            {app.isAI && app.category !== 'models' && (
              <div className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI
              </div>
            )}
            {app.isNew && (
              <div className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                New
              </div>
            )}
          </div>
        </div>

        {/* App Info */}
        <div className="flex-1">
          <div className="flex items-start flex-col gap-2">
            <h3 className="text-lg font-semibold text-gray-900 leading-tight">
              {app.name}
            </h3>
            {app.category && (
              <span className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-600 text-xs font-medium">
                {categories.find(cat => cat.id === app.category)?.name}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-600 line-clamp-3">
            {app.description}
          </p>
        </div>

        {/* Footer */}
        <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center text-sm text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
            Try Now
            <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {app.rating && (
            <div className="flex items-center text-sm text-gray-500">
              <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {app.rating}
            </div>
          )}
        </div>
      </div>

      {/* Hover Effect */}
      <div 
        className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        style={{
          background: `linear-gradient(135deg, ${auraColors.from}10, ${auraColors.to}10)`,
          pointerEvents: 'none'
        }}
      />
    </div>
  );
};

// Define categories with actual implemented features
const categories = [
  { id: 'education', name: 'Education Tools' },
  { id: 'writing', name: 'Writing & Formatting' },
  { id: 'development', name: 'Development Tools' },
  { id: 'nonprofit', name: 'Nonprofit & Grants' },
  { id: 'productivity', name: 'Productivity & AI Tools' },
  { id: 'models', name: 'AI Models' }
];

// Featured AI apps with only implemented pages
const featuredApps = {
  featured: [
    {
      id: 'principal-ai',
      name: 'Principal AI',
      icon: '🤖',
      description: 'Advanced AI system for strategic decision making and leadership insights',
      category: 'productivity',
      isAI: true,
      brand: 'company',
      path: '/principal-ai',
      isNew: true
    },
    {
      id: 'ai-toybox',
      name: 'AI Assistant',
      icon: '🤖',
      description: 'Your intelligent AI companion for writing, analysis, coding, and more. Powered by advanced language models.',
      category: 'productivity',
      isAI: true,
      brand: 'company',
      path: '/ai-toybox'
    },
    {
      id: 'resume-builder',
      name: 'Resume Builder',
      icon: '📄',
      description: 'Create professional, ATS-optimized resumes with AI assistance',
      category: 'writing',
      isAI: true,
      brand: 'company',
      path: '/resume-builder'
    },
    {
      id: 'auto-rag',
      name: 'AutoRAG',
      icon: '📚',
      description: 'Drop files to automatically analyze, parse, and summarize content using RAG technology',
      category: 'development',
      isAI: true,
      brand: 'company',
      path: '/auto-rag'
    },
    {
      id: 'sql-writer',
      name: 'SQL Writer',
      icon: '📊',
      description: 'Generate optimized SQL queries with AI assistance',
      category: 'development',
      isAI: true,
      brand: 'company',
      path: '/sql-writer'
    }
  ],
  education: [
    {
      id: 'lesson-plan-creator',
      name: 'Lesson Plan Creator',
      icon: '📚',
      description: 'Creates ready-to-use lesson plans for teachers to save time and engage students',
      category: 'education',
      isAI: true,
      brand: 'company',
      path: '/lesson-plan-creator'
    },
    {
      id: 'course-outline-creator',
      name: 'Course Outline Creator',
      icon: '📝',
      description: 'Creates organized learning plans with clear goals for teachers to guide their lessons',
      category: 'education',
      isAI: true,
      brand: 'company',
      path: '/course-outline-creator'
    },
    {
      id: 'training-manual-creator',
      name: 'Training Manual Creator',
      icon: '📖',
      description: 'Generate comprehensive training manuals and documentation',
      category: 'education',
      isAI: true,
      brand: 'company',
      path: '/training-manual-creator'
    }
  ],
  writing: [
    {
      id: 'story-weaver',
      name: 'Story Weaver',
      icon: '✍️',
      description: 'AI-powered creative writing assistant for stories, articles, and content',
      category: 'writing',
      isAI: true,
      brand: 'company',
      path: '/creative-writer'
    },
    {
      id: 'play-formatter',
      name: 'Play Formatter',
      description: 'Transform raw text into properly formatted play scripts and screenplays',
      category: 'writing',
      isAI: true,
      brand: 'company',
      path: '/play-formatter'
    },
    {
      id: 'powerpoint-generator',
      name: 'Slide Generator',
      icon: '📊',
      description: 'Generate professional presentations with AI assistance',
      category: 'writing',
      isAI: true,
      brand: 'company',
      path: '/powerpoint-generator'
    }
  ],
  development: [
    {
      id: 'test-script-generator',
      name: 'Test Script Generator',
      icon: '🧪',
      description: 'Generate comprehensive test scripts for your software with AI assistance',
      category: 'development',
      isAI: true,
      brand: 'company',
      path: '/test-script-generator'
    },
    {
      id: 'web-crafter',
      name: 'WebCrafter',
      icon: '🌐',
      description: 'Interactive HTML code editor with AI assistance for rapid web development',
      category: 'development',
      isAI: true,
      brand: 'company',
      path: '/web-crafter'
    },
    {
      id: 'pyspark-writer',
      name: 'PySpark Writer',
      icon: '⚡',
      description: 'Create efficient PySpark transformations and analytics code',
      category: 'development',
      isAI: true,
      brand: 'company',
      path: '/pyspark-writer'
    },
    {
      id: 'data-dictionary-generator',
      name: 'Data Dictionary Generator',
      icon: '📚',
      description: 'Generate comprehensive data dictionaries and sample data with AI assistance',
      category: 'development',
      isAI: true,
      brand: 'company',
      path: '/data-dictionary-generator'
    }
  ],
  nonprofit: [
    {
      id: 'grant-writer',
      name: 'Grant Writer',
      icon: '📝',
      description: 'Get AI help writing compelling grant proposals',
      category: 'nonprofit',
      isAI: true,
      brand: 'company',
      path: '/grant-writer'
    },
    {
      id: 'grant-finder',
      name: 'Grant Finder',
      icon: '🔍',
      description: 'Discover grants tailored to your organization using AI matching',
      category: 'nonprofit',
      isAI: true,
      brand: 'company',
      path: '/grant-finder'
    },
    {
      id: 'rfp-manager',
      name: 'RFP Manager',
      icon: '📋',
      description: 'Manage and respond to RFPs efficiently with AI assistance',
      category: 'nonprofit',
      isAI: true,
      brand: 'company',
      path: '/RFPManager'
    }
  ],
  productivity: [
    {
      id: 'life-coach',
      name: 'Life Coach',
      description: 'Accelerate your career growth with data-driven guidance and insights',
      category: 'productivity',
      isAI: true,
      brand: 'company',
      path: '/life-coach'
    },
    {
      id: 'social-media-manager',
      name: 'Social Media Manager',
      icon: '🔄',
      description: 'Create and manage social media content with AI assistance',
    category: 'productivity',
      isAI: true,
      brand: 'company',
      path: '/social-media-manager'
    },
    {
      id: 'research-assistant',
      name: 'Research Assistant',
      icon: '🔍',
      description: 'AI-powered research and analysis assistant',
    category: 'productivity',
      isAI: true,
      brand: 'company',
      path: '/research-assistant'
    }
  ],
  models: [
    {
      id: 'claude-3-7',
      name: 'Claude 3.7 Sonnet',
      icon: '🧠',
      description: 'Anthropic\'s state-of-the-art language model with superior reasoning capabilities, context window of 200K tokens, and competitive pricing.',
      category: 'models',
      isAI: true,
      brand: 'company',
      path: 'https://huggingface.co/anthropic/claude-3-sonnet-20240229'
    },
    {
      id: 'gpt-o1',
      name: 'GPT-o1',
      icon: '🔮',
      description: 'OpenAI\'s most advanced model with multimodal capabilities, high accuracy, and deep reasoning for complex tasks.',
      category: 'models',
      isAI: true,
      brand: 'company',
      path: 'https://huggingface.co/openai-community/gpt4-x-alpaca-13b-native-4bit'
    },
    {
      id: 'mistral-large',
      name: 'Mistral Large',
      icon: '🌪️',
      description: 'Mistral AI\'s flagship model offering enterprise-grade capabilities with excellent performance-to-cost ratio.',
      category: 'models',
      isAI: true,
      brand: 'company',
      path: 'https://huggingface.co/mistralai/Mistral-7B-Instruct-v0.2'
    },
    {
      id: 'llama-3',
      name: 'Llama 3 70B',
      icon: '🦙',
      description: 'Meta\'s powerful open foundation model that delivers excellent performance across various tasks with 70B parameters.',
      category: 'models',
      isAI: true,
      brand: 'company',
      path: 'https://huggingface.co/meta-llama/Llama-2-70b-hf'
    },
    {
      id: 'deepseek',
      name: 'DeepSeek Coder',
      icon: '👨‍💻',
      description: 'Specialized model for software development with deep understanding of numerous programming languages and frameworks.',
      category: 'models',
      isAI: true,
      brand: 'company',
      path: 'https://huggingface.co/deepseek-ai/deepseek-coder-33b-instruct'
    },
    {
      id: 'gpt-o3-mini',
      name: 'GPT-o3 Mini',
      icon: '✨',
      description: 'Lightweight and cost-effective model from OpenAI with excellent capabilities for most common tasks.',
      category: 'models',
      isAI: true,
      brand: 'company',
      path: 'https://huggingface.co/openai-community/gpt4-x-alpaca-7b-native-4bit'
    }
  ]
};

// Add section descriptions
const sectionDescriptions = {
  featured: "Our most popular tools and models to enhance your productivity.",
  education: "Tools designed specifically for educators, teachers and trainers to streamline content creation.",
  writing: "Enhance your writing with AI-powered tools for content creation and formatting.",
  development: "Developer tools to accelerate coding, database management, and technical documentation.",
  nonprofit: "Resources specialized for nonprofit organizations to find and secure funding.",
  productivity: "General productivity tools to optimize your workflow and save time.",
  models: "Access raw AI model capabilities directly for custom use cases and development."
};

// Remove all enterprise apps and combine only our implemented apps
const applications = Object.values(featuredApps).flat();

// Add this constant at the top with other constants
const INTEGRABLE_SERVICES = {
  slack: {
    name: 'Slack',
    description: 'Messages & Threads',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
    ),
    color: 'purple'
  },
  teams: {
    name: 'Teams',
    description: 'Chats & Meetings',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    color: 'indigo'
  },
  jira: {
    name: 'Jira',
    description: 'Tasks & Sprints',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    color: 'blue'
  },
  asana: {
    name: 'Asana',
    description: 'Projects & Tasks',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    color: 'rose'
  },
  notion: {
    name: 'Notion',
    description: 'Docs & Notes',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    color: 'gray'
  },
  github: {
    name: 'GitHub',
    description: 'PRs & Issues',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
      </svg>
    ),
    color: 'zinc'
  }
};

// Update the MetricCard component
const MetricCard = ({ service, data }) => {
  const { name, description, icon, color } = INTEGRABLE_SERVICES[service];
  const { value, trend, notification } = data;
  
  return (
    <div className={`
      relative group bg-white rounded-xl p-4 border transition-all duration-200
      hover:border-${color}-200 hover:shadow-lg
      ${notification ? 'border-${color}-200' : 'border-gray-100'}
      flex-1 min-w-[200px]
    `}>
      {/* Notification Badge */}
    {notification && (
        <div className={`
          absolute -top-2 -right-2 w-6 h-6 rounded-full bg-${color}-500 text-white
          text-xs font-bold flex items-center justify-center shadow-sm
        `}>
        {notification}
      </div>
    )}

      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className={`
          p-2 rounded-lg bg-${color}-50 text-${color}-600
          group-hover:bg-${color}-100 transition-colors duration-200
        `}>
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between">
          <div>
              <p className="text-sm font-medium text-gray-900">{name}</p>
              <p className="text-xs text-gray-500">{description}</p>
          </div>
            {trend && (
              <span className={`
                text-xs font-medium flex items-center
                ${trend > 0 ? 'text-green-600' : 'text-red-600'}
              `}>
                {Math.abs(trend)}%
                <svg className={`w-3 h-3 ml-0.5 ${trend > 0 ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
                      </span>
                    )}
                  </div>
          <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
                </div>
                </div>
              </div>
  );
};

// Add styled component for the right sidebar
const RightSidebar = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 240px;
  background: white;
  border-left: 1px solid #e5e7eb;
  overflow-y: auto;
  padding: 1rem;
  z-index: 40;
`;

// Update the MainContent styled component
const MainContent = styled.div`
  margin-left: 64px; // Keep margin for left sidebar
  min-height: 100vh;
  background: #ffffff;
`;

// Add this after the existing styled components
const CollapsedRightSidebar = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 56px;
  background: white;
  border-left: 1px solid #e5e7eb;
  z-index: 40;
  transition: width 0.2s ease;
  
  &:hover {
    width: 200px;
    
    .category-text {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const CategoryButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
  
  .category-icon {
    min-width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .category-text {
    opacity: 0;
    transform: translateX(10px);
    transition: all 0.2s ease;
    white-space: nowrap;
    margin-left: 0.75rem;
  }
`;

const CategoryToolbar = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 64px;
  background: #0f0f0f;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 0;
  z-index: 40;
  border-left: 1px solid rgba(255, 255, 255, 0.1);

  .category-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-center: center;
    color: #666;
    margin: 0.25rem 0;
    border-radius: 8px;
    transition: all 0.2s ease;
    position: relative;
    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;
    }

    &:hover, &.active {
      color: #fff;
      background: rgba(255, 255, 255, 0.05);
    }

    &::after {
      content: attr(data-tooltip);
      position: absolute;
      right: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 6px 10px;
      border-radius: 6px;
      font-size: 12px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }

    &:hover::after {
      opacity: 1;
    }
  }
`;

// Update the StickyCategoryBar styled component
const StickyCategoryBar = styled.div`
  position: sticky;
  top: 80px; // Update to account for header height
  z-index: 30;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
  margin-bottom: 2rem;
`;

export default function Dashboard() {
  const { user, userPreferences, updateSelectedApps } = useAuth();
  const navigate = useNavigate();
  const [showAppSelector, setShowAppSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleAppClick = (app) => {
    if (!app || !app.id) {
      console.error('Invalid app data');
      return;
    }

    // Handle external URLs (like Hugging Face model pages)
    if (app.path.startsWith('http')) {
      window.open(app.path, '_blank');
      return;
    }

    // Handle internal routes
    const targetPath = app.path || `/${app.id}`;
    const cleanPath = targetPath.replace(/\/+/g, '/');
    navigate(cleanPath, {
      state: { appData: app },
      replace: false
    });
  };

  const getFilteredApps = (categoryId) => {
    if (categoryId === 'all') {
      // Return unique apps by id
      return Array.from(new Map(applications.map(app => [app.id, app])).values());
    }
    
    if (categoryId === 'featured') {
      return featuredApps.featured;
    }

    if (categoryId === 'models') {
      return featuredApps.models;
    }

    // Get all apps that match the selected category from featuredApps
    const allApps = Object.values(featuredApps).flat();
    
    // Filter by category and remove duplicates based on app.id
    const filteredApps = allApps.filter(app => app.category === categoryId);
    return Array.from(new Map(filteredApps.map(app => [app.id, app])).values());
  };

  // Get filtered apps memo
  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const searchLower = searchQuery.toLowerCase();
    const results = new Map(); // Use Map to prevent duplicates
    
    // Helper function to add app to results if it matches
    const addIfMatches = (app) => {
      if (!results.has(app.id)) {
        const matchesName = app.name.toLowerCase().includes(searchLower);
        const matchesDescription = app.description?.toLowerCase().includes(searchLower);
        const matchesCategory = app.category?.toLowerCase().includes(searchLower);
        
        if (matchesName || matchesDescription || matchesCategory) {
          // Add score to prioritize matches
          app.searchScore = (matchesName ? 3 : 0) + 
                          (matchesDescription ? 1 : 0) + 
                          (matchesCategory ? 1 : 0);
          results.set(app.id, app);
        }
      }
    };
    
    // Search in featured apps first
    Object.values(featuredApps).forEach(group => {
      group.forEach(addIfMatches);
    });
    
    // Then search in other apps
    applications.forEach(addIfMatches);
    
    // Convert to array, sort by score, and take top 8 results
    return Array.from(results.values())
      .sort((a, b) => b.searchScore - a.searchScore)
      .slice(0, 8);
  }, [searchQuery]);

  // Update the blur handler to use a ref for better timing
  const searchBlurTimeout = useRef(null);
  
  const handleSearchBlur = () => {
    if (searchBlurTimeout.current) {
      clearTimeout(searchBlurTimeout.current);
    }
    searchBlurTimeout.current = setTimeout(() => {
      setIsSearchFocused(false);
      setSearchQuery('');
    }, 200);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchBlurTimeout.current) {
        clearTimeout(searchBlurTimeout.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <MainContent>
        {/* Modern Header */}
        <div className="relative bg-white text-gray-900 overflow-hidden">
          {/* Enhanced Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/80 via-purple-50/80 to-pink-50/80 mix-blend-multiply"></div>
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-purple-500/5 via-blue-500/5 to-transparent"></div>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-blue-500/5 via-purple-500/5 to-transparent"></div>
            </div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDAsIDAsIDAsIDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <div className="space-y-8 max-w-4xl">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-gray-200/50 backdrop-blur-sm">
                <div className="flex items-center space-x-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                  </span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 font-mono text-sm font-medium tracking-wider">AI WORKSPACE</span>
                </div>
              </div>
              
              {/* Main Heading */}
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">
                  <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 animate-gradient pb-2">
                    Augment Your Reality
                  </span>
            </h1>
                <p className="text-xl sm:text-2xl md:text-3xl text-gray-600 font-light max-w-3xl">
                  with <span className="font-medium text-gray-900">Intelligent Tools</span> designed to enhance your workflow
                </p>
              </div>

              {/* Enhanced Search with Visual Elements */}
              <div className="relative max-w-2xl mt-12">
                <div className="absolute -inset-1">
                  <div className="w-full h-full mx-auto rotate-180 opacity-30 blur-lg filter">
                    <div className="aspect-w-16 aspect-h-8 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
                  </div>
                </div>
              <div className="relative">
                  <div className="flex items-center bg-white border border-gray-200 rounded-xl shadow-sm transition-all duration-200 hover:border-purple-200 hover:shadow-lg">
                    <input
                      type="text"
                      placeholder="Search AI tools and capabilities..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={handleSearchBlur}
                      className="w-full bg-transparent text-gray-900 placeholder-gray-500 px-6 py-4 focus:outline-none text-lg rounded-xl"
                    />
                    <div className="pr-6 pl-2 flex items-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors duration-200">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                  {/* Quick Links */}
                  <div className="mt-4 flex items-center space-x-4 text-sm text-gray-600">
                    <span className="font-medium">Popular:</span>
                    <div className="flex items-center space-x-3">
                      {['AI Assistant', 'SQL Writer', 'Test Generator'].map((item) => (
                        <button
                          key={item}
                          onClick={() => setSearchQuery(item)}
                          className="px-3 py-1 rounded-full border border-gray-200 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-600 transition-colors duration-200"
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search Results - Fixed Positioning */}
                {searchQuery && filteredApps.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl">
                      <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
                        {filteredApps.map((app) => (
                        <div
                          key={app.id}
                          onClick={() => {
                            handleAppClick(app);
                            setSearchQuery('');
                            setIsSearchFocused(false);
                          }}
                            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="w-8 h-8 flex-shrink-0 rounded-lg bg-gray-50 flex items-center justify-center text-gray-600">
                              {getIcon(app.id)}
                            </div>
                              <div>
                                <h3 className="text-gray-900 font-medium">{app.name}</h3>
                                <p className="text-sm text-gray-600 line-clamp-1">{app.description}</p>
                          </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Sticky Category Bar */}
        <StickyCategoryBar>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4 overflow-x-auto hide-scrollbar">
            <button
              onClick={() => setSelectedCategory('all')}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
                  transition-all duration-200
                  ${selectedCategory === 'all' 
                    ? 'bg-gray-900 text-white' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
            >
              All Apps
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                  className={`
                    px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
                    transition-all duration-200
                    ${selectedCategory === category.id 
                      ? 'bg-gray-900 text-white' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
        </StickyCategoryBar>

        {/* Rest of the content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {selectedCategory === 'all' ? (
          <>
            {/* Featured Apps Section */}
              <div className="mb-16 relative">
                <div className="flex items-center mb-2">
                  <div className="h-1 w-8 bg-gradient-to-r from-blue-500 to-purple-500 mr-2"></div>
                  <span className="text-sm font-medium text-gray-500">FEATURED</span>
                </div>
              <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-gray-600 mt-1 text-sm">{sectionDescriptions.featured}</p>
                  </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const container = document.getElementById('featured-apps-container');
                      container.scrollBy({ left: -300, behavior: 'smooth' });
                    }}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      const container = document.getElementById('featured-apps-container');
                      container.scrollBy({ left: 300, behavior: 'smooth' });
                    }}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Scrollable container */}
              <div 
                id="featured-apps-container"
                className="flex overflow-x-auto gap-8 pb-6 hide-scrollbar scroll-smooth"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {Object.values(featuredApps).flat().map(app => (
                  // Featured app card with fixed height
                  <div 
                    key={app.id}
                    className="flex-none w-[320px]"
                    style={{ scrollSnapAlign: 'start' }}
                  >
                    <AppCard
                      app={app}
                      onClick={handleAppClick}
                    />
                  </div>
                ))}
              </div>

              {/* Scroll progress indicators */}
              <div className="hidden sm:flex justify-center gap-1.5 mt-6">
                {Array.from({ length: Math.ceil(Object.values(featuredApps).flat().length / 3) }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      const container = document.getElementById('featured-apps-container');
                      container.scrollTo({ left: i * (320 * 3), behavior: 'smooth' });
                    }}
                      className="w-8 h-1 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors duration-200"
                    />
                  ))}
                </div>
              </div>

              {/* Models Section */}
              <div className="mb-16 relative">
                <div className="flex items-center mb-2">
                  <div className="h-1 w-8 bg-gradient-to-r from-blue-500 to-purple-500 mr-2"></div>
                  <span className="text-sm font-medium text-gray-500">MODELS</span>
                </div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-gray-600 mt-1 text-sm">{sectionDescriptions.models}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const container = document.getElementById('models-container');
                        container.scrollBy({ left: -300, behavior: 'smooth' });
                      }}
                      className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        const container = document.getElementById('models-container');
                        container.scrollBy({ left: 300, behavior: 'smooth' });
                      }}
                      className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors duration-200"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Models scrollable container */}
                <div
                  id="models-container"
                  className="flex overflow-x-auto gap-8 pb-6 hide-scrollbar scroll-smooth"
                  style={{
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {featuredApps.models.map(model => (
                    <div 
                      key={model.id}
                      className="flex-none w-[320px]"
                      style={{ scrollSnapAlign: 'start' }}
                    >
                      <AppCard
                        app={model}
                        onClick={handleAppClick}
                      />
                    </div>
                  ))}
                </div>

                {/* Scroll progress indicators */}
                <div className="hidden sm:flex justify-center gap-1.5 mt-6">
                  {Array.from({ length: Math.ceil(featuredApps.models.length / 3) }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        const container = document.getElementById('models-container');
                        container.scrollTo({ left: i * (320 * 3), behavior: 'smooth' });
                      }}
                      className="w-8 h-1 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors duration-200"
                  />
                ))}
              </div>
            </div>

            {/* All Categories */}
            {categories.map(category => {
              const categoryApps = getFilteredApps(category.id);
                if (categoryApps.length === 0 || category.id === 'featured' || category.id === 'models') return null;

              return (
                  <div key={category.id} className="mb-16 relative">
                    <div className="flex items-center mb-2">
                      <div className="h-1 w-8 bg-gradient-to-r from-blue-500 to-purple-500 mr-2"></div>
                      <span className="text-sm font-medium text-gray-500">{category.id.toUpperCase()}</span>
                    </div>
                  <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className="text-gray-600 mt-1 text-sm">{sectionDescriptions[category.id]}</p>
                  </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const container = document.getElementById(`${category.id}-apps-container`);
                            container.scrollBy({ left: -300, behavior: 'smooth' });
                          }}
                          className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            const container = document.getElementById(`${category.id}-apps-container`);
                            container.scrollBy({ left: 300, behavior: 'smooth' });
                          }}
                          className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div 
                      id={`${category.id}-apps-container`}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                      {categoryApps.map((app) => (
                      <div key={app.id}>
                        <AppCard
                          app={app}
                          onClick={handleAppClick}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          // Show only the selected category's apps
          <div>
              <div className="flex items-center mb-2">
                <div className="h-1 w-8 bg-gradient-to-r from-blue-500 to-purple-500 mr-2"></div>
                <span className="text-sm font-medium text-gray-500">
                  {(selectedCategory || 'CATEGORY').toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {categories.find(cat => cat.id === selectedCategory)?.name || 'Apps'}
            </h2>
                <p className="text-gray-600 mb-6 text-sm">
                  {sectionDescriptions[selectedCategory] || 'Browse available applications and tools.'}
                </p>
              </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {selectedCategory === 'featured' ? (
                Object.values(featuredApps).flat().map(app => (
                  <div key={app.id}>
                    <AppCard
                      app={app}
                      onClick={handleAppClick}
                    />
                  </div>
                ))
              ) : (
                getFilteredApps(selectedCategory).map(app => (
                  <div key={app.id}>
                    <AppCard
                      app={app}
                      onClick={handleAppClick}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      </MainContent>

      {/* App Selector Modal */}
      {showAppSelector && (
        <div className="fixed inset-0 z-50 bg-white/80">
        <AppSelector
          onClose={() => setShowAppSelector(false)}
          applications={applications}
          selectedApps={userPreferences?.selectedApps || []}
          onSave={updateSelectedApps}
          categories={categories}
        />
        </div>
      )}

      {/* Existing styles */}
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        @media (hover: none) {
          .hide-scrollbar {
            -webkit-overflow-scrolling: touch;
          }
        }

        /* Gradient Animation */
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 8s ease infinite;
        }
      `}</style>
    </div>
  );
}

// Add this helper function for category icons
function getCategoryIcon(categoryId) {
  switch (categoryId) {
    case 'education':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      );
    case 'writing':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    case 'development':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      );
    case 'nonprofit':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'productivity':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      );
  }
}

